import { useReactiveVar } from '@apollo/client';
import React, { useState, useEffect } from 'react';

import TableHeader from '@/components/TableHeader';
import Table from '@/components/Table';
import { TableRow, TableCell } from '@/elements/Table';
import Card from '@/components/Card';
import LoadingState from '@/components/LoadingState';
import Page from '@/components/Page';
import Stack from '@/components/Stack';
import { Body } from '@/components/Typography';
import TextStack from '@/components/TextStack';
import useMediaQuery from '@/hooks/useMediaQuery';
import { currentAgencyVar } from '@/util/apollo/cache';
import { axiosRubyClient } from '@/util/axios/axiosClient';
import { format } from 'date-fns';
import Chip from '@/components/Chip';
import Link from '@/elements/Link';
import IconicButton from '@/components/IconicButton';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import ReportModal from './ReportModal';
import TextInput from '@/components/TextInput';
import Pagination from '@/components/Pagination';

const HEADER_FIELDS = [
  { name: 'ID', style: { width: '10%' } },
  { name: 'WORKER ID', style: { width: '25%', color: 'black' } },
  { name: 'PREVIOUS STATUS', style: { width: '15%', color: 'black' } },
  { name: 'NEW STATUS', style: { width: '15%', color: 'black' } },
  { name: 'NOTES', style: { width: '25%', color: 'black' } },
  { name: 'DATE', style: { width: '20%', color: 'black' } },
  { name: 'ACTIONS', style: { width: '10%', color: 'black' } },
];

type WorkBrightCheckLog = {
  id: string;
  worker_id: number;
  report_data: any;
  previous_status: string;
  new_status: string;
  notes: string;
  created_at: string;
  updated_at: string;
  worker: {
    user: {
      first_name: string;
      middle_name: string;
      last_name: string;
    };
  };
};

type PaginationData = {
  total_count: number;
  current_page: number;
  per_page: number;
  total_pages: number;
};

const formatDate = (dateString: string) => {
  try {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
  } catch (e) {
    return dateString;
  }
};

const getStatusChip = (status: string) => {
  if (!status) return <Chip label="Unknown" size="small" />;

  switch (status.toLowerCase()) {
    case 'pending':
      return <Chip color="warning" label="Pending" size="small" />;
    case 'approved':
      return <Chip color="success" label="Approved" size="small" />;
    case 'rejected':
      return <Chip color="error" label="Rejected" size="small" />;
    case 'needs_attention':
      return <Chip color="error" label="Needs Attention" size="small" />;
    case 'tentative_nc':
      return <Chip color="warning" label="Tentative NC" size="small" />;
    case 'in_progress':
      return <Chip color="warning" label="In Progress" size="small" />;
    case 'cancelled':
      return <Chip color="error" label="Cancelled" size="small" />;
    case 'pending_e-verify':
    case 'pending_everify':
      return <Chip color="warning" label="Pending E-Verify" size="small" />;
    case 'pending_section2':
      return <Chip color="warning" label="Pending Section 2" size="small" />;
    case 'e-verify_in_progress':
    case 'everify_in_progress':
      return <Chip color="warning" label="E-Verify In Progress" size="small" />;
    case 'prospect':
      return <Chip color="default" label="Prospect" size="small" />;
    case 'closed':
      return <Chip color="default" label="Closed" size="small" />;
    case 'n/a':
    case 'na':
      return <Chip color="default" label="N/A" size="small" />;
    default:
      return (
        <Chip
          style={{ textTransform: 'capitalize' }}
          label={status.replace(/_/g, ' ')}
          size="small"
        />
      );
  }
};

const WorkBrightAudit: React.FC = () => {
  const currentAgency = useReactiveVar(currentAgencyVar);
  const phoneOnly = useMediaQuery('(max-width: 559px)');
  const [loading, setLoading] = useState(true);
  const [workbrightLogs, setWorkbrightLogs] = useState<WorkBrightCheckLog[]>(
    [],
  );
  const [error, setError] = useState<string | null>(null);
  const [selectedReportData, setSelectedReportData] = useState<any>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [pagination, setPagination] = useState<PaginationData>({
    total_count: 0,
    current_page: 1,
    per_page: 25,
    total_pages: 0,
  });
  const [query, setQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    fetchWorkbrightLogs();
  }, []);

  // Debounce search with 500ms delay
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput !== query) {
        setQuery(searchInput);
        setPagination((prev) => ({ ...prev, current_page: 1 })); // Reset to first page on search
        fetchWorkbrightLogs(searchInput, 1);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput, query]);

  const fetchWorkbrightLogs = async (
    searchQuery?: string,
    page: number = 1,
  ) => {
    setLoading(true);
    try {
      const response = await axiosRubyClient.get('/api/workbright_check_logs', {
        params: {
          query: searchQuery || query,
          page,
          per_page: pagination.per_page,
        },
      });
      setWorkbrightLogs(response.data.data);

      // Update pagination data from response
      if (response.data.pagination) {
        setPagination({
          total_count: response.data.pagination.total_count,
          current_page: response.data.pagination.current_page,
          per_page: response.data.pagination.per_page || pagination.per_page,
          total_pages: Math.ceil(
            response.data.pagination.total_count /
              (response.data.pagination.per_page || pagination.per_page),
          ),
        });
      }

      setError(null);
    } catch (error) {
      console.error('Error fetching WorkBright check logs:', error);
      setError('Error loading WorkBright check logs');
    } finally {
      setLoading(false);
    }
  };

  const handleShowReport = (reportData: any) => {
    console.log('Showing report data:', reportData);
    setSelectedReportData(reportData);
    setShowReportModal(true);
  };

  const handleHideModal = () => {
    setShowReportModal(false);
    setSelectedReportData(null);
  };

  const onQueryChange = (value: string) => {
    setSearchInput(value);
  };

  const onPageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, current_page: page }));
    fetchWorkbrightLogs(query, page);
  };

  return (
    <Page
      size="full"
      title="WorkBright Audit"
      primaryAction={{
        a11yLabel: 'Refresh',
        label: 'Refresh',
        onAction: () => fetchWorkbrightLogs(query, pagination.current_page),
      }}
    >
      <Card>
        <Card.Section>
          <Stack gap={phoneOnly ? 16 : 8} vertical={phoneOnly}>
            <div style={{ flex: 1 }}>
              <TextInput
                id="input-search-worker"
                placeholder={`Search for GravyWorkers...`}
                value={searchInput}
                onChange={(ev) => onQueryChange(ev.currentTarget.value)}
              />
            </div>
          </Stack>
        </Card.Section>

        <Table spacing={false}>
          <TableHeader fields={HEADER_FIELDS} />
          <tbody>
            {!loading && workbrightLogs.length > 0 ? (
              workbrightLogs.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <Stack gap={20}>
                      <TextStack spacing="tight">
                        <Body weight="medium">{item.id}</Body>
                      </TextStack>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack gap={20}>
                      <TextStack spacing="tight">
                        <Link
                          id={`link-${item.id}`}
                          to={`/workers/${item.worker_id}`}
                        >
                          <Body color={'theme'} weight="medium">
                            {item.worker.user.first_name}{' '}
                            {item.worker.user.middle_name}{' '}
                            {item.worker.user.last_name}
                          </Body>
                        </Link>
                      </TextStack>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack gap={20}>
                      <TextStack spacing="tight">
                        {getStatusChip(item.previous_status)}
                      </TextStack>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack gap={20}>
                      <TextStack spacing="tight">
                        {getStatusChip(item.new_status)}
                      </TextStack>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack gap={20}>
                      <TextStack spacing="tight">
                        <Body>{item.notes}</Body>
                      </TextStack>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack gap={20}>
                      <TextStack spacing="tight">
                        <Body>{formatDate(item.created_at)}</Body>
                      </TextStack>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack gap={20}>
                      <TextStack spacing="tight">
                        <IconicButton
                          a11yLabel="View Report Data"
                          appearance="primary"
                          icon={faEye}
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShowReport(item.report_data);
                          }}
                        />
                      </TextStack>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            ) : !loading ? (
              <TableRow>
                <TableCell
                  colSpan={HEADER_FIELDS.length}
                  style={{ textAlign: 'center', padding: '20px' }}
                >
                  <Body>No WorkBright check logs found.</Body>
                </TableCell>
              </TableRow>
            ) : null}
          </tbody>
        </Table>

        {loading && (
          <Stack align="center" justify="center" style={{ padding: '40px 0' }}>
            <LoadingState
              overlayColor="white"
              text="Loading WorkBright check logs"
            />
          </Stack>
        )}

        {error && (
          <div style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
            <Body>{error}</Body>
          </div>
        )}

        {/* Pagination */}
        {!loading &&
          workbrightLogs.length > 0 &&
          pagination.total_pages > 1 && (
            <Card.Section>
              <Stack justify="center">
                <Pagination
                  currentPage={pagination.current_page}
                  totalPages={pagination.total_pages}
                  onPageChange={onPageChange}
                />
              </Stack>
            </Card.Section>
          )}
      </Card>

      <ReportModal
        reportData={selectedReportData}
        isOpen={showReportModal}
        onClose={handleHideModal}
      />
    </Page>
  );
};

export default WorkBrightAudit;
